import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackAddToUserPlan } from '@/store/tracks/actions';

import { Button, Skeleton } from 'antd';
import CalendarModal from '@/pages/CalendarModal';

import { EXPIRED, FINISHED, IN_PROGRESS } from './constants';

import useImage from '@shared/hooks/useImage';

import Utils from '@/Utils';
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import css from './CollectionTrackCard.module.scss';

const MAX_TITLE_LENGTH = 120;

const SkeletonTrackCard = () => (
  <div className={classNames(css['CollectionTrackCard'], css['CollectionTrackCard-skeleton'])}>
    <Skeleton.Image active className={css['CollectionTrackCard-skeleton-image']} />
    <div className={css['CollectionTrackCard-content']}>
      <Skeleton active paragraph={{ rows: 2 }} />

      <div className={css['CollectionTrackCard-content-footer']}>
        <Skeleton.Button active block size='large' />
      </div>
    </div>
  </div>
);

const CollectionTrackCard = ({ track, onAddToPlanSuccess }) => {
  const { t } = useTranslation('collectionTrackCard');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  const onAddToPlanTrackClick = e => {
    e.stopPropagation();
    setCalendarModalOpen(true);
  };

  const trackImage = useImage(track?.coverImage, 'track');

  const handleAddToPlan = (_, selectedMonths) => {
    const params = {
      trackId: track?.id,
      startDate: selectedMonths?.toString(),
    };

    dispatch(
      trackAddToUserPlan(params, () => {
        setCalendarModalOpen(false);
        onAddToPlanSuccess();
      })
    );
  };

  const trackCardButton = useMemo(() => {
    switch (track?.trackStudentPlanStatus) {
      case EXPIRED:
      case IN_PROGRESS:
      case FINISHED:
        return (
          <Button className={css['CollectionTrackCard-button']} size='large' ghost>
            {track?.trackStudentPlanName}
          </Button>
        );

      default:
        return (
          <Button
            className={css['CollectionTrackCard-button']}
            size='large'
            type='primary'
            onClick={onAddToPlanTrackClick}
          >
            {t('addToPlan')}
          </Button>
        );
    }
  }, [track]);

  const onCardClick = () => {
    navigate(`/track/${track?.id}`, {
      state: {
        returnSearch: searchParams.toString(),
        fromCollectionTracks: true,
      },
    });
  };

  const correctTrackDuration = track?.trackDuration > 0 ? track?.trackDuration - 1 : 0; // Для избежания ошибки, если trackDuration = 0

  const minTrackDate = dayjs().add(correctTrackDuration, 'day');

  return (
    <>
      <div data-qa='collectionTrackCard' className={css['CollectionTrackCard']} onClick={onCardClick}>
        <img className={css['CollectionTrackCard-image']} src={trackImage} alt={track?.name} />
        <div className={css['CollectionTrackCard-content']}>
          <h5 className={css['CollectionTrackCard-content-title']}>
            {Utils.renderWithTooltip(track?.name, MAX_TITLE_LENGTH)}
          </h5>

          <div className={css['CollectionTrackCard-content-footer']}>
            <span className={css['CollectionTrackCard-content-footer-counter']}>
              {t('materialsCount', { count: track?.topicsCount })}
            </span>
            {trackCardButton}
          </div>
        </div>
      </div>

      {calendarModalOpen && (
        <CalendarModal
          open={calendarModalOpen}
          onOk={handleAddToPlan}
          title={t('trackDeadlineTitle')}
          onCancel={() => setCalendarModalOpen(false)}
          okText={t('addToPlanTrack')}
          selectMonthEvents
          minTrackDate={minTrackDate}
        />
      )}
    </>
  );
};

CollectionTrackCard.Skeleton = SkeletonTrackCard;

CollectionTrackCard.propTypes = {
  track: PropTypes.object.isRequired,
  onAddToPlanSuccess: PropTypes.func.isRequired,
};

export default CollectionTrackCard;
